/**
 * Email HTML templates per report type.
 *
 * Each tool defines its own email_template key in TABLE_DATA.
 * The template function receives TableData and returns full HTML string.
 *
 * ⚠️ DS-TOKEN-02 exception: e-mails HTML precisam de hex hardcoded — CSS
 * custom properties não são suportadas por todos os clientes de e-mail
 * (Outlook, Gmail web antigo). As cores usadas aqui correspondem à paleta
 * MDi: `#03154a` = navy-800 oficial, `#ffffff` = white, demais = escala
 * gray Tailwind. Qualquer mudança visual de e-mail deve preservar Navy MDi
 * como cor de header institucional.
 */

import type { TableData, TableColumn } from '../types/tableData'

const MDI_LOGO_BASE64 =
  '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'

function formatBrl(value: number): string {
  return value
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

function formatCellForEmail(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === '') return '\u2014'
  if (format === 'currency_brl' && typeof value === 'number') {
    return `R$ ${formatBrl(value)}`
  }
  return String(value)
}

function emailShell(content: string, title: string, subtitle?: string): string {
  const now = new Date()
  const dateStr = now.toLocaleDateString('pt-BR')
  const timeStr = now.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background-color:#f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width:900px;margin:0 auto;background-color:#ffffff;">
    <tr>
      <td style="background-color:#03154a;padding:20px 32px;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="vertical-align:middle;">
              <img src="data:image/png;base64,${MDI_LOGO_BASE64}" alt="MDi Iveco" height="50" style="display:block;height:50px;width:auto;" />
            </td>
            <td style="text-align:right;vertical-align:middle;">
              <span style="font-size:14px;font-weight:600;color:#ffffff;letter-spacing:-0.3px;">Optimus</span>
              <br><span style="font-size:11px;color:#9ca3af;">${dateStr} ${timeStr}</span>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:28px 32px 8px;">
        <h1 style="margin:0;font-size:20px;font-weight:700;color:#03154a;">${title}</h1>
        ${subtitle ? `<p style="margin:6px 0 0;font-size:13px;color:#6b7280;">${subtitle}</p>` : ''}
      </td>
    </tr>
    ${content}
    <tr>
      <td style="padding:0 32px 24px;">
        <p style="margin:0;font-size:11px;color:#9ca3af;line-height:1.5;">
          Relatorio gerado automaticamente pelo MDi Optimus em ${dateStr} as ${timeStr}.
        </p>
      </td>
    </tr>
    <tr>
      <td style="background-color:#f9fafb;padding:16px 32px;border-top:1px solid #e5e7eb;">
        <p style="margin:0;font-size:11px;color:#9ca3af;text-align:center;">
          MDi Iveco &mdash; Optimus Plataforma de IA Multi-Agente
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`.trim()
}

// ==================== OS em Aberto ====================

function buildOsAbertoEmail(data: TableData): string {
  const headerCells = data.columns
    .map(
      (col: TableColumn) =>
        `<th style="padding:8px 12px;text-align:${col.align || 'left'};font-size:12px;color:#ffffff;border-bottom:2px solid #1a2a5e;">${col.label}</th>`
    )
    .join('')

  const bodyRows = data.rows
    .map((row, i) => {
      const bgColor = i % 2 === 0 ? '#ffffff' : '#f8f9fa'
      const cells = data.columns
        .map((col: TableColumn) => {
          const val = formatCellForEmail(row[col.key], col.format)
          const isDays = col.key === 'dias_aberto'
          const numVal = row[col.key] as number | null
          const isWarning = isDays && numVal != null && numVal > 7
          const style = `padding:6px 12px;text-align:${col.align || 'left'};font-size:12px;border-bottom:1px solid #e5e7eb;${isWarning ? 'color:#dc2626;font-weight:700;' : 'color:#1f2937;'}`
          return `<td style="${style}">${isWarning ? '\u26A0 ' : ''}${val}</td>`
        })
        .join('')
      return `<tr style="background-color:${bgColor}">${cells}</tr>`
    })
    .join('')

  const summaryHtml = `
    <tr>
      <td style="padding:12px 32px 20px;">
        <table cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <tr>
            <td style="padding:12px 20px;background-color:#fef2f2;border-right:1px solid #e5e7eb;">
              <span style="font-size:20px;font-weight:700;color:#e8372e;">${data.summary?.total || data.rows.length}</span>
              <br><span style="font-size:11px;color:#6b7280;">OS em aberto</span>
            </td>
            ${
              data.summary?.max_days
                ? `<td style="padding:12px 20px;background-color:#fffbeb;border-right:1px solid #e5e7eb;">
              <span style="font-size:20px;font-weight:700;color:#d97706;">${data.summary.max_days}</span>
              <br><span style="font-size:11px;color:#6b7280;">Dias (mais antiga)</span>
            </td>`
                : ''
            }
            ${
              data.summary?.total_value
                ? `<td style="padding:12px 20px;background-color:#f0fdf4;">
              <span style="font-size:20px;font-weight:700;color:#059669;">R$ ${formatBrl(data.summary.total_value)}</span>
              <br><span style="font-size:11px;color:#6b7280;">Valor total</span>
            </td>`
                : ''
            }
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 24px;">
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;border-collapse:collapse;">
          <thead><tr style="background-color:#03154a;">${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 24px;">
        <p style="margin:0;font-size:11px;color:#9ca3af;">O arquivo XLSX com os dados completos esta em anexo.</p>
      </td>
    </tr>`

  return emailShell(summaryHtml, data.title, data.subtitle)
}

// ==================== HDs em Aberto ====================

function buildHdsAbertoEmail(data: TableData): string {
  const total = data.summary?.total || data.rows.length

  const content = `
    <tr>
      <td style="padding:12px 32px 20px;">
        <table cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <tr>
            <td style="padding:12px 20px;background-color:#fef2f2;border-right:1px solid #e5e7eb;">
              <span style="font-size:20px;font-weight:700;color:#e8372e;">${total}</span>
              <br><span style="font-size:11px;color:#6b7280;">HDs em aberto</span>
            </td>
            ${
              data.summary?.max_days
                ? `<td style="padding:12px 20px;background-color:#fffbeb;">
              <span style="font-size:20px;font-weight:700;color:#d97706;">${data.summary.max_days}</span>
              <br><span style="font-size:11px;color:#6b7280;">Dias (HD mais antigo)</span>
            </td>`
                : ''
            }
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 24px;">
        <p style="margin:0;font-size:13px;color:#374151;line-height:1.6;">
          Segue em anexo a planilha com o relatorio completo de <strong>${total} HD${total !== 1 ? 's' : ''}</strong> em aberto,
          contendo todos os dados extraidos do portal IvecoCS (Rel Unidade Parada).
        </p>
      </td>
    </tr>`

  return emailShell(content, data.title, data.subtitle)
}

// ==================== Default (fallback) ====================

function buildDefaultEmail(data: TableData): string {
  return buildOsAbertoEmail(data)
}

// ==================== Registry ====================

type EmailTemplateBuilder = (data: TableData) => string

// ==================== Pedidos CSPS ====================
// Apenas anexo XLSX no corpo (decisão da Maira: tabela só na planilha).

function buildPedidosCspsEmail(data: TableData): string {
  const summary = data.summary
  const total = summary?.total ?? data.rows.length
  const atribuidos = summary?.atribuido_count ?? 0
  const backorders = summary?.backorder_count ?? 0
  const faturados = summary?.faturado_count ?? 0
  const anulados = summary?.anulado_count ?? 0

  const statCell = (
    value: number,
    label: string,
    bg: string,
    color: string,
    border = true
  ) => `
    <td style="padding:12px 20px;background-color:${bg};${border ? 'border-right:1px solid #e5e7eb;' : ''}">
      <span style="font-size:20px;font-weight:700;color:${color};">${value}</span>
      <br><span style="font-size:11px;color:#6b7280;">${label}</span>
    </td>`

  const content = `
    <tr>
      <td style="padding:12px 32px 20px;">
        <table cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <tr>
            ${statCell(total, 'Pedidos no período', '#eef2ff', '#03154a')}
            ${atribuidos ? statCell(atribuidos, 'Atribuídos', '#f5f3ff', '#7c3aed') : ''}
            ${backorders ? statCell(backorders, 'Backorder', '#fef2f2', '#dc2626') : ''}
            ${faturados ? statCell(faturados, 'Faturados', '#f0fdf4', '#059669') : ''}
            ${anulados ? statCell(anulados, 'Anulados', '#fff7ed', '#ea580c', false) : ''}
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:0 32px 24px;">
        <p style="margin:0;font-size:13px;color:#374151;line-height:1.6;">
          Segue em anexo a planilha completa com <strong>${total} pedido${total !== 1 ? 's' : ''}</strong> extraídos
          do portal CSPS (Linhas Pedido), com status colorido por linha igual ao seu controle manual.
        </p>
      </td>
    </tr>`

  return emailShell(content, data.title, data.subtitle)
}

const TEMPLATES: Record<string, EmailTemplateBuilder> = {
  os_aberto: buildOsAbertoEmail,
  hds_aberto: buildHdsAbertoEmail,
  pedidos_csps: buildPedidosCspsEmail,
}

/**
 * Build email HTML for the given table data.
 * Uses `data.email_template` to select the right template.
 */
export function buildEmailHtml(data: TableData): string {
  const builder = TEMPLATES[data.email_template || ''] || buildDefaultEmail
  return builder(data)
}
